import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';

import { ApiError } from '@api/errors/api-error';
import { tokenStorage } from '@api/lib/token-storage';
import { useAuthStore } from '@stores/auth-store';

import { env } from '@config/env';

// ─── Dedicated auth client (no interceptors) ────────────────────────────────
// Used exclusively for token refresh calls. Must NOT share interceptors with
// the main httpClient — doing so would cause infinite 401 retry loops and
// a circular module dependency (http → interceptors → auth.api → http).
const authClient = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'app-type': 'Garage',
  },
});

// ─── Concurrent refresh queue ────────────────────────────────────────────
// Requests that arrive while a refresh is in flight are held here.
// On success each is retried with the new token; on failure each is rejected.

interface PendingEntry {
  config: InternalAxiosRequestConfig & { _retry?: boolean };
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

let isRefreshing = false;
let pendingQueue: PendingEntry[] = [];

// mainClient is set once by setupInterceptors so the response handler
// can retry failed requests without importing httpClient directly.
let mainClient: AxiosInstance;

function flushQueue(newToken: string): void {
  pendingQueue.forEach(({ config, resolve }) => {
    config.headers.Authorization = `Bearer ${newToken}`;
    resolve(mainClient(config));
  });
  pendingQueue = [];
}

function rejectQueue(reason: unknown): void {
  pendingQueue.forEach(({ reject }) => reject(reason));
  pendingQueue = [];
}

// ─── Setup ───────────────────────────────────────────────────────────────────

export function setupInterceptors(client: AxiosInstance): void {
  mainClient = client; // capture for retry calls inside onResponseError
  client.interceptors.request.use(onRequest, onRequestError);
  client.interceptors.response.use(onResponse, onResponseError);
}

// ─── Request ────────────────────────────────────────────────────────────────

function onRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const { accessToken, workshop } = useAuthStore.getState();

  config.headers['app-type'] = 'Garage';

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  if (workshop) {
    config.headers['X-Workshop-Id'] = workshop.id;
    // Only send optional context headers when values are available
    if (workshop.branchId) config.headers['X-Branch-Id'] = workshop.branchId;
    if (workshop.financialYearId) config.headers['X-Financial-Year-Id'] = workshop.financialYearId;
  }

  return config;
}

function onRequestError(error: unknown): Promise<never> {
  // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
  return Promise.reject(error);
}

// ─── Response ────────────────────────────────────────────────────────────────

function onResponse(response: AxiosResponse): AxiosResponse {
  return response;
}

async function onResponseError(error: unknown): Promise<unknown> {
  if (!isAxiosErrorShape(error)) {
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
    return Promise.reject(error);
  }

  const status = error.response?.status ?? 0;
  const originalConfig = error.config as
    | (InternalAxiosRequestConfig & { _retry?: boolean })
    | undefined;

  // ── Token refresh on 401 ────────────────────────────────────────────────────
  // _retry flag prevents infinite loops on the refresh call itself.
  if (status === 401 && originalConfig && !originalConfig._retry) {
    const storedRefreshToken = tokenStorage.getRefreshToken();

    if (storedRefreshToken) {
      // ── Queue if another refresh is already in flight ─────────────────
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ config: originalConfig, resolve, reject });
        });
      }

      originalConfig._retry = true;
      isRefreshing = true;

      try {
        // Use the interceptor-free authClient to avoid circular calls.
        const res = await authClient.post<{
          success: 0 | 1;
          message: string;
          data: { accessToken: string; refreshToken?: string | null };
        }>(
          '/workshop/auth/refresh-token',
          {},
          {
            headers: { 'refresh-token': storedRefreshToken },
          },
        );

        const newAccessToken = res.data.data.accessToken;
        useAuthStore.getState().updateToken(newAccessToken);
        // Cache the refreshed access token for the rest of this tab session.
        tokenStorage.setAccessToken(newAccessToken);
        originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;

        // Sliding refresh: if the backend rotated the refresh token (within 48 h),
        // persist the new one. If null, the existing token is kept unchanged.
        const rotatedRefreshToken =
          res.data?.data?.refreshToken ??
          (res.headers['refresh-token'] as string | undefined) ??
          null;
        if (rotatedRefreshToken && rotatedRefreshToken !== 'null') {
          tokenStorage.setRefreshToken(rotatedRefreshToken);
        }

        // Retry all queued requests with the new token, then retry this one.
        flushQueue(newAccessToken);
        return mainClient(originalConfig);
      } catch (refreshError) {
        rejectQueue(refreshError);
        tokenStorage.removeRefreshToken();
        tokenStorage.removeAccessToken();
        useAuthStore.getState().logout();
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // No stored refresh token — session is unrecoverable.
    tokenStorage.removeRefreshToken();
    useAuthStore.getState().logout();
  }

  const apiError = ApiError.fromResponse({
    status,
    data: error.response?.data as { message?: string; code?: string; errors?: unknown },
  });

  return Promise.reject(apiError);
}

// ─── Type guard ────────────────────────────────────────────────────────────────

function isAxiosErrorShape(error: unknown): error is {
  response?: { status: number; data: unknown };
  config?: InternalAxiosRequestConfig;
} {
  return typeof error === 'object' && error !== null && 'response' in error;
}
